// ============================================================================
// CHANGO V5.0 — Customer detail page (D6 frontend) + 双语字段编辑
// ============================================================================
// Mount: /app/customers/:id
//
// Shows the customer header with bilingual field edit, tag editor, contact list,
// and activity timeline. The "tags" PATCH uses replace-all semantics.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api, HttpError } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';
import RoleGate from '../../components/RoleGate.jsx';
import TimelineFeed from '../../components/TimelineFeed.jsx';
import NotFound from '../NotFound.jsx';
import CustomerForm from './CustomerForm.jsx';

const MAX_TAGS = 20;
const MAX_TAG_LEN = 40;

function TagEditor({ tags, onSave, saving }) {
  const [draft, setDraft] = useState('');
  const [local, setLocal] = useState(tags || []);
  const [err, setErr] = useState(null);

  useEffect(() => { setLocal(tags || []); }, [tags]);

  const addTag = () => {
    const t = draft.trim();
    if (!t) return;
    if (t.length > MAX_TAG_LEN) { setErr(`标签最长 ${MAX_TAG_LEN} 字符`); return; }
    if (local.includes(t)) { setDraft(''); return; }
    if (local.length >= MAX_TAGS) { setErr(`最多 ${MAX_TAGS} 个标签`); return; }
    setLocal([...local, t]);
    setDraft('');
    setErr(null);
  };
  const removeTag = (t) => setLocal(local.filter((x) => x !== t));

  const save = async () => {
    try {
      setErr(null);
      await onSave(local);
    } catch (e) {
      setErr(e?.message || '保存失败');
    }
  };

  const dirty = useMemo(() => {
    const a = [...(tags || [])].sort().join('|');
    const b = [...local].sort().join('|');
    return a !== b;
  }, [tags, local]);

  return (
    <section className="card p-4">
      <header className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-800">标签</h3>
        <span className="text-xs text-slate-400">{local.length} / {MAX_TAGS}</span>
      </header>
      <div className="flex flex-wrap gap-2 mb-3">
        {local.length === 0 ? (
          <span className="text-xs text-slate-400">暂无标签</span>
        ) : local.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
            {t}
            <button
              type="button"
              aria-label={`移除 ${t}`}
              className="text-slate-400 hover:text-rose-600"
              onClick={() => removeTag(t)}
            >×</button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          className="field flex-1"
          placeholder="输入标签后回车或点击添加"
          maxLength={MAX_TAG_LEN}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
        />
        <button type="button" className="btn-ghost text-sm" onClick={addTag} disabled={!draft.trim()}>
          添加
        </button>
        <button type="button" className="btn-primary text-sm" onClick={save} disabled={!dirty || saving}>
          {saving ? '保存中…' : '保存'}
        </button>
      </div>
      {err && <p className="err text-sm mt-2">{err}</p>}
    </section>
  );
}

function ContactForm({ initial, onSubmit, onCancel, busy }) {
  const [f, setF] = useState({
    contact_name: initial?.contact_name || '',
    title: initial?.title || '',
    email: initial?.email || '',
    phone: initial?.phone || '',
    wechat: initial?.wechat || '',
    whatsapp: initial?.whatsapp || '',
    is_primary: !!initial?.is_primary,
    notes: initial?.notes || ''
  });
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.contact_name.trim()) { setErr('姓名必填'); return; }
    try {
      await onSubmit(f);
    } catch (e2) {
      setErr(e2?.message || '保存失败');
    }
  };

  const fld = (k, props = {}) => (
    <input
      type="text"
      className="field"
      value={f[k] || ''}
      onChange={(e) => setF({ ...f, [k]: e.target.value })}
      {...props}
    />
  );

  return (
    <form onSubmit={submit} className="rounded border border-slate-200 p-3 space-y-2 bg-slate-50">
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs">姓名*{fld('contact_name', { maxLength: 120 })}</label>
        <label className="text-xs">职务{fld('title', { maxLength: 120 })}</label>
        <label className="text-xs">邮箱{fld('email', { type: 'email', maxLength: 200 })}</label>
        <label className="text-xs">电话{fld('phone', { maxLength: 60 })}</label>
        <label className="text-xs">微信{fld('wechat', { maxLength: 120 })}</label>
        <label className="text-xs">WhatsApp{fld('whatsapp', { maxLength: 60 })}</label>
      </div>
      <label className="text-xs block">
        备注
        <textarea
          className="field"
          rows={2}
          maxLength={500}
          value={f.notes}
          onChange={(e) => setF({ ...f, notes: e.target.value })}
        />
      </label>
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={f.is_primary} onChange={(e) => setF({ ...f, is_primary: e.target.checked })} />
        设为主要联系人（将自动取消其他主要联系人）
      </label>
      {err && <p className="err text-sm">{err}</p>}
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-ghost text-sm" onClick={onCancel} disabled={busy}>取消</button>
        <button type="submit" className="btn-primary text-sm" disabled={busy}>
          {busy ? '保存中…' : '保存'}
        </button>
      </div>
    </form>
  );
}

function ContactsSection({ customerId, role }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const canDelete = role === 'boss' || role === 'finance';

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const data = await api.get(`/api/customers/${customerId}/contacts`);
      setContacts(Array.isArray(data?.contacts) ? data.contacts : []);
    } catch (e) {
      setErr(e instanceof HttpError ? e.message : '加载联系人失败');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const create = async (f) => {
    setBusy(true);
    try {
      await api.post(`/api/customers/${customerId}/contacts`, f);
      setAdding(false);
      await load();
    } finally { setBusy(false); }
  };

  const saveEdit = async (id, f) => {
    setBusy(true);
    try {
      await api.patch(`/api/customers/${customerId}/contacts/${id}`, f);
      setEditingId(null);
      await load();
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('删除该联系人？不可恢复。')) return;
    try {
      await api.delete(`/api/customers/${customerId}/contacts/${id}`);
      await load();
    } catch (e) {
      setErr(e?.message || '删除失败');
    }
  };

  return (
    <section className="card p-4">
      <header className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-800">联系人 · {contacts.length}</h3>
        {!adding && (
          <button type="button" className="btn-ghost text-sm" onClick={() => setAdding(true)}>
            新增联系人
          </button>
        )}
      </header>

      {err && <p className="err text-sm mb-2">{err}</p>}
      {loading ? (
        <p className="text-sm text-slate-500">加载中…</p>
      ) : contacts.length === 0 && !adding ? (
        <p className="text-sm text-slate-500">尚未添加联系人。</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {contacts.map((c) => (
            <li key={c.id} className="py-3">
              {editingId === c.id ? (
                <ContactForm
                  initial={c}
                  busy={busy}
                  onCancel={() => setEditingId(null)}
                  onSubmit={(f) => saveEdit(c.id, f)}
                />
              ) : (
                <div className="flex items-start justify-between gap-3 text-sm">
                  <div>
                    <div className="font-medium text-slate-800">
                      {c.contact_name}
                      {c.is_primary && (
                        <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-800">
                          主要
                        </span>
                      )}
                    </div>
                    {c.title && <div className="text-xs text-slate-500">{c.title}</div>}
                    <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-slate-600">
                      {c.email && <div><dt className="inline text-slate-400">邮箱：</dt><dd className="inline">{c.email}</dd></div>}
                      {c.phone && <div><dt className="inline text-slate-400">电话：</dt><dd className="inline">{c.phone}</dd></div>}
                      {c.wechat && <div><dt className="inline text-slate-400">微信：</dt><dd className="inline">{c.wechat}</dd></div>}
                      {c.whatsapp && <div><dt className="inline text-slate-400">WhatsApp：</dt><dd className="inline">{c.whatsapp}</dd></div>}
                    </dl>
                    {c.notes && <p className="mt-1 text-xs text-slate-600 whitespace-pre-wrap">{c.notes}</p>}
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <button type="button" className="text-slate-500 hover:text-slate-800" onClick={() => setEditingId(c.id)}>编辑</button>
                    {canDelete && (
                      <button type="button" className="text-rose-500 hover:text-rose-700" onClick={() => remove(c.id)}>删除</button>
                    )}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <div className="mt-3">
          <ContactForm
            busy={busy}
            onCancel={() => setAdding(false)}
            onSubmit={create}
          />
        </div>
      )}
    </section>
  );
}

export default function CustomerDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const hasValidId = typeof id === 'string' && /^[1-9]\d*$/.test(id);
  const customerId = hasValidId ? id : null;
  const [customer, setCustomer] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [savingTags, setSavingTags] = useState(false);
  const [savingHeader, setSavingHeader] = useState(false);
  const [editingHeader, setEditingHeader] = useState(false);

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true); setErr(null);
    try {
      const [customerData, groupsData] = await Promise.all([
        api.get(`/api/customers/${customerId}`),
        api.get('/api/customer-groups')
      ]);
      setCustomer(customerData?.customer ?? customerData);
      setGroups(groupsData?.items || []);
    } catch (e) {
      setErr(e instanceof HttpError ? e.message : '加载客户失败');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  const saveHeader = useCallback(async (formData) => {
    if (!customerId) return;
    setSavingHeader(true);
    try {
      await api.patch(`/api/customers/${customerId}`, formData);
      await load();
      setEditingHeader(false);
    } finally {
      setSavingHeader(false);
    }
  }, [customerId, load]);

  const saveTags = useCallback(async (tags) => {
    if (!customerId) return;
    setSavingTags(true);
    try {
      await api.patch(`/api/customers/${customerId}/tags`, { tags });
      await load();
    } finally {
      setSavingTags(false);
    }
  }, [customerId, load]);

  useEffect(() => { load(); }, [load]);

  if (!hasValidId) {
    return <NotFound homeHref="/app/customers" homeLabel="返回客户列表" />;
  }
  if (loading) return <div className="p-6 text-sm text-slate-500">加载中…</div>;
  if (err)     return <div className="p-6 err">{err}</div>;
  if (!customer) return <div className="p-6 text-sm text-slate-500">未找到客户。</div>;

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="mb-6 flex items-center justify-between">
        <div />
        <Link to="/app/customers" className="btn-ghost text-sm">返回列表</Link>
      </div>

      {editingHeader ? (
        <div className="mb-6">
          <CustomerForm
            initialCustomer={customer}
            groups={groups}
            saving={savingHeader}
            submitLabel="保存"
            onCancel={() => setEditingHeader(false)}
            onSubmit={saveHeader}
          />
        </div>
      ) : (
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-800">
              {customer?.company_name || '未命名客户'}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {customer?.customer_code && `客户编号: ${customer.customer_code} · `}
              {customer?.country_code ? `${customer.country_code} · ` : ''}
              {customer?.industry || '未分类行业'}
            </p>
          </div>
          <button type="button" className="btn-ghost text-sm" onClick={() => setEditingHeader(true)}>
            编辑基本信息
          </button>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <TagEditor tags={customer.tags || []} onSave={saveTags} saving={savingTags} />
          <ContactsSection customerId={customerId} role={user?.role} />
        </div>
        <div>
          <RoleGate allow={['boss', 'finance', 'sales']}>
            <TimelineFeed scope="entity" entityType="customer" entityId={customerId} pageSize={20} />
          </RoleGate>
        </div>
      </div>
    </div>
  );
}
